import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { axioKit } from "../../../../../services/utilities";

const MEDICINES_URL = "commerce/catalog/medicines";

const STATUS = {
  approved: { label: "Available", color: "success" },
  pending: { label: "Pending approval", color: "warning" },
  halt: { label: "On hold", color: "secondary" },
  banned: { label: "Banned", color: "danger" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "approved", label: "Available" },
  { key: "pending", label: "Pending" },
  { key: "unavailable", label: "On hold / banned" },
];

const title = (value = "") =>
  String(value).replace(/\b\w/g, (char) => char.toUpperCase());

// "10's · 25 grams / box"
const packLabel = ({ pack = {} } = {}) => {
  const { q, v, u, b } = pack || {};
  const size = [q && `${q}'s`, v && `${v}${u ? ` ${u}` : ""}`]
    .filter(Boolean)
    .join(" · ");
  return [size, b && `per ${b}`].filter(Boolean).join(" ");
};

// a banned generic bans its brands; else the brand status, then the generic's
const statusOf = ({ status, genericId }) =>
  genericId?.status === "banned"
    ? "banned"
    : status || genericId?.status || "approved";

/**
 * Merchandise > Medicines: the formulary of the branch, every brand of
 * medicine with its generic, strength, form, route and use, so the frontdesk
 * can answer what is carried here. Brands are managed by the company admin.
 */
export default function Medicines() {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    branchId = activePlatform?.branchId,
    [medicines, setMedicines] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState(""),
    [drugClass, setDrugClass] = useState(""),
    [form, setForm] = useState(""),
    { addToast } = useToasts();

  const load = useCallback(() => {
    if (!token || !branchId) return;
    setIsLoading(true);
    axioKit
      .universal(`${MEDICINES_URL}/browse`, token, { branchId })
      .then(({ payload = [] }) => setMedicines(payload))
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [token, branchId, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const options = (key) =>
    [...new Set(medicines.map(({ genericId }) => genericId?.[key]).filter(Boolean))].sort();
  const classes = useMemo(() => options("drugClass"), [medicines]); // eslint-disable-line react-hooks/exhaustive-deps
  const forms = useMemo(() => options("dosageForm"), [medicines]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const result = { "": medicines.length, approved: 0, pending: 0, unavailable: 0 };
    medicines.forEach((medicine) => {
      const status = statusOf(medicine);
      if (status === "approved") result.approved += 1;
      else if (status === "pending") result.pending += 1;
      else result.unavailable += 1;
    });
    return result;
  }, [medicines]);

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return medicines
      .filter((medicine) => {
        const generic = medicine.genericId || {};
        const status = statusOf(medicine);
        if (filter === "unavailable" && ["approved", "pending"].includes(status))
          return false;
        if (filter && filter !== "unavailable" && status !== filter) return false;
        if (drugClass && generic.drugClass !== drugClass) return false;
        if (form && generic.dosageForm !== form) return false;
        const text = [
          medicine.name,
          medicine.subname,
          medicine.barcode,
          medicine.purpose,
          generic.name,
          generic.indication,
          generic.drugClass,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return words.every((word) => text.includes(word));
      })
      .sort(
        (a, b) =>
          String(a.genericId?.name || a.name).localeCompare(
            String(b.genericId?.name || b.name),
          ) || String(a.name).localeCompare(String(b.name)),
      );
  }, [medicines, search, filter, drugClass, form]);

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="pills" className="mr-2 text-primary" />
              Medicines
            </h5>
            <small className="text-muted">
              Medicines carried by this branch: generic, strength, form and use.
            </small>
          </div>
          <MDBBtn size="sm" color="light" className="m-0" onClick={load} disabled={isLoading}>
            <MDBIcon icon="sync" spin={isLoading} className="mr-1" /> Refresh
          </MDBBtn>
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Status">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  filter === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => setFilter(key)}
              >
                {label}
                <MDBBadge
                  color={filter === key ? "light" : "primary"}
                  className={`ml-2 ${filter === key ? "text-dark" : ""}`}
                >
                  {counts[key]}
                </MDBBadge>
              </button>
            ))}
          </div>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            {classes.length > 0 && (
              <select
                className="browser-default custom-select custom-select-sm"
                style={{ width: 170 }}
                value={drugClass}
                onChange={({ target }) => setDrugClass(target.value)}
                aria-label="Drug class"
              >
                <option value="">All drug classes</option>
                {classes.map((value) => (
                  <option key={value} value={value}>
                    {title(value)}
                  </option>
                ))}
              </select>
            )}
            {forms.length > 0 && (
              <select
                className="browser-default custom-select custom-select-sm"
                style={{ width: 140 }}
                value={form}
                onChange={({ target }) => setForm(target.value)}
                aria-label="Dosage form"
              >
                <option value="">All forms</option>
                {forms.map((value) => (
                  <option key={value} value={value}>
                    {title(value)}
                  </option>
                ))}
              </select>
            )}
            <div className="position-relative" style={{ width: 250 }}>
              <MDBIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30 }}
                placeholder="Search brand, generic, use, barcode"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </div>
          </div>
        </div>

        <MDBTable responsive hover small className="mb-0">
          <thead className="grey lighten-4">
            <tr>
              <th>Generic</th>
              <th>Brand</th>
              <th>Form · route</th>
              <th>Used for</th>
              <th>Packaging</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-5">
                  {isLoading
                    ? "Loading medicines…"
                    : !medicines.length
                      ? "No medicine registered for this branch yet."
                      : "No medicine matches your filters."}
                </td>
              </tr>
            )}
            {visible.map((medicine) => {
              const generic = medicine.genericId || {};
              const status = STATUS[statusOf(medicine)] || STATUS.approved;
              return (
                <tr key={medicine._id}>
                  <td className="align-middle">
                    <span className="font-weight-bold">
                      {generic.name || <span className="text-muted">No generic</span>}
                    </span>
                    {generic.strength && (
                      <span className="ml-1">{generic.strength}</span>
                    )}
                    {generic.drugClass && (
                      <small className="d-block text-muted">{title(generic.drugClass)}</small>
                    )}
                  </td>
                  <td className="align-middle">
                    {medicine.name}
                    {medicine.subname && (
                      <small className="d-block text-muted">{medicine.subname}</small>
                    )}
                    {medicine.barcode && (
                      <small className="d-block text-muted">
                        <MDBIcon icon="barcode" className="mr-1" />
                        {medicine.barcode}
                      </small>
                    )}
                  </td>
                  <td className="align-middle small">
                    {[generic.dosageForm, generic.route].filter(Boolean).map(title).join(" · ") || (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="align-middle small" style={{ maxWidth: 280 }}>
                    {medicine.purpose || generic.indication || (
                      <span className="text-muted">—</span>
                    )}
                    {generic.SystemTarget && (
                      <span className="d-block text-muted">
                        {title(generic.SystemTarget)} system
                      </span>
                    )}
                  </td>
                  <td className="align-middle small">
                    {packLabel(medicine.packages) || <span className="text-muted">—</span>}
                    {medicine.packages?.hasRetail && (
                      <span className="d-block text-muted">sold per piece too</span>
                    )}
                  </td>
                  <td className="align-middle">
                    <MDBBadge color={status.color}>{status.label}</MDBBadge>
                    {medicine.halt && (
                      <small className="d-block text-muted">{medicine.halt}</small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>
      </MDBCardBody>
    </MDBCard>
  );
}
